import React from 'react';
import {
  CABINET,
  CABINET_HARDWARE,
} from '../../3d/geometry/proceduralRenderers/proceduralSpecs';
import { cabinetFrameRailWidth } from '../../3d/geometry/proceduralRenderers/cabinetFrontLayout';
import { Circle, Line, Rect } from './SchematicPrimitives';

/**
 * Door-style insets, hardware glyphs and the toe-kick line for cabinet
 * schematics. Every dimension is the 3D renderer's own (via `CABINET` /
 * `CABINET_HARDWARE` and `cabinetFrameRailWidth`), so the drawn frame and
 * pull are the mesh's frame and pull in orthographic projection.
 *
 * Geometry is in metres in the parent viewBox; callers pass positions
 * already mapped into that viewBox (top-left origin, y down).
 */

/**
 * Framed door styles in FRONT elevation. `x`/`y`/`width`/`height` are the
 * front's outer rectangle.
 *   - flat: nothing (a slab has no inner line)
 *   - shaker / slim-shaker: the frame's inner edge — the recessed panel
 *     outline (a slim shaker's sits just inside its thin edge)
 *   - raised-panel: the frame's inner edge plus the raised field, which the
 *     mesh sets `CABINET.raisedPanelInset` inside it
 *   - glass-front: the frame's inner edge (the pane) with the drafting
 *     glazing mark — two short diagonals in one corner
 */
export function DoorStyleInset({
  x,
  y,
  width,
  height,
  style,
  isDrawer = false,
  strokeColor,
  strokeWidth,
}) {
  if (style === 'flat') return null;
  const r = cabinetFrameRailWidth(width, height, isDrawer, style);
  const panel = {
    x: x + r,
    y: y + r,
    width: width - 2 * r,
    height: height - 2 * r,
  };
  if (panel.width <= 0 || panel.height <= 0) return null;
  const stroke = { strokeColor, strokeWidth };

  return (
    <g data-door-style={style}>
      <Rect {...panel} {...stroke} />
      {style === 'raised-panel' && <RaisedField panel={panel} {...stroke} />}
      {style === 'glass-front' && <GlazingMark panel={panel} {...stroke} />}
    </g>
  );
}

function RaisedField({ panel, strokeColor, strokeWidth }) {
  const inset = CABINET.raisedPanelInset;
  const width = panel.width - 2 * inset;
  const height = panel.height - 2 * inset;
  if (width <= 0 || height <= 0) return null;
  return (
    <Rect
      x={panel.x + inset}
      y={panel.y + inset}
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    />
  );
}

/** Drafting glazing symbol: two short parallel diagonals in the pane's corner. */
function GlazingMark({ panel, strokeColor, strokeWidth }) {
  const run = Math.min(panel.width, panel.height) * 0.3;
  const step = run * 0.35;
  const x0 = panel.x + panel.width * 0.12;
  const y0 = panel.y + panel.height - panel.height * 0.12;
  const diagonal = (offset) => (
    <Line
      key={offset}
      x1={x0 + offset}
      y1={y0}
      x2={x0 + offset + run}
      y2={y0 - run}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    />
  );
  return <g>{[0, step].map(diagonal)}</g>;
}

/**
 * Hardware in FRONT elevation. `placement` is `cabinetHardwarePlacement`'s
 * result with `x` / `y` already mapped into the viewBox.
 */
export function HardwareGlyph({ placement, strokeColor, strokeWidth }) {
  const { kind, x, y } = placement;
  const stroke = { strokeColor, strokeWidth };
  if (kind === 'knob') {
    return <Circle cx={x} cy={y} r={placement.radius} {...stroke} />;
  }
  if (kind === 'cup') {
    // The cup's dome arches up from its open mouth (the chord).
    const half = placement.length / 2;
    return (
      <g>
        <path
          d={`M ${x - half} ${y} A ${half} ${placement.height} 0 0 1 ${x + half} ${y}`}
          fill="none"
          stroke={strokeColor}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
        <Line x1={x - half} y1={y} x2={x + half} y2={y} {...stroke} />
      </g>
    );
  }
  // bar: a rounded slot along the hinge stile (doors) or across the front (drawers).
  const vertical = placement.orientation === 'vertical';
  const w = vertical ? placement.diameter : placement.length;
  const h = vertical ? placement.length : placement.diameter;
  return (
    <rect
      x={x - w / 2}
      y={y - h / 2}
      width={w}
      height={h}
      rx={placement.diameter / 2}
      fill="none"
      stroke={strokeColor}
      strokeWidth={strokeWidth}
      vectorEffect="non-scaling-stroke"
    />
  );
}

/**
 * Hardware in SIDE elevation: what stands proud of the door face. Drawn in
 * the canonical LEFT view (door face at x = 0, the room toward -x), at the
 * placement's mapped `y`. A bar pull seen end-on is a circle; along its
 * length, a slot; a knob a circle; a cup pull its quarter-ellipse dome
 * profile with the open mouth underneath.
 */
export function HardwareProfile({ placement, strokeColor, strokeWidth }) {
  const { kind, y } = placement;
  const gap = CABINET_HARDWARE.frontGap;
  const stroke = { strokeColor, strokeWidth };
  if (kind === 'knob') {
    return (
      <Circle
        cx={-(gap + placement.radius)}
        cy={y}
        r={placement.radius}
        {...stroke}
      />
    );
  }
  if (kind === 'cup') {
    const top = y - placement.height / 2;
    const bottom = y + placement.height / 2;
    const apex = -(gap + placement.projection);
    return (
      <path
        d={`M ${-gap} ${top} A ${placement.projection} ${placement.height} 0 0 0 ${apex} ${bottom} L ${-gap} ${bottom} Z`}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
    );
  }
  const r = placement.diameter / 2;
  if (placement.orientation === 'horizontal') {
    return <Circle cx={-(gap + r)} cy={y} r={r} {...stroke} />;
  }
  return (
    <rect
      x={-(gap + placement.diameter)}
      y={y - placement.length / 2}
      width={placement.diameter}
      height={placement.length}
      rx={r}
      fill="none"
      stroke={strokeColor}
      strokeWidth={strokeWidth}
      vectorEffect="non-scaling-stroke"
    />
  );
}

/**
 * Toe-kick line for lower cabinets in FRONT elevation: the carcass bottom
 * edge, `CABINET.toeKickHeight` above the floor, spanning the full width
 * (the recessed kick face is visible below it).
 */
export function CabinetToeKick({ width, height, strokeColor, strokeWidth }) {
  const kick = CABINET.toeKickHeight;
  if (height <= kick) return null;
  return (
    <Line
      x1={0}
      y1={height - kick}
      x2={width}
      y2={height - kick}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    />
  );
}
